import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/core';
import * as MobileDetect from 'mobile-detect';

import { CimaCore, Cloudinary, Modal, Scroll, Util, XmStore, XmStoreUtil } from 'core/services';
import { AccountPlanEligibility, User } from 'store/user/models';
import { CmsActivity, CmsCore, CmsMyAccount } from 'store/cms/models';
import { Transaction, TransactionCollection } from 'store/transaction/models';
import { AddonRouteType, BannerAction, DataPlanType, GlobalTravelPassTabName, LINES_LIST_FIRST_LOAD_LIMIT, UnlPlanSubgroup } from 'my-account/constants';
import { AccountSummary, LineSummary, PlanSummary } from 'store/account-summary/models';
import { StoreAction } from 'store/actions';
import { Decorator } from 'services/decorator';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { XmEnvLinkPipe } from 'shared/pipes/env-link';
import { GlobalTravelPass, GlobalTravelPassTab } from 'store/global-travel-pass/models/global-travel-pass';
import { GtpLineOptionClick } from '../../shared/gtp-card/gtp-card';
import { BillingBannerInfo } from 'store/billing-banner/models';

@Component({
    selector: 'my-account-activity',
    styleUrls: ['./landing.scss'],
    templateUrl: './landing.html',
    standalone: false
})
export class ActivityComponent implements OnInit, OnDestroy {
    public cmsLineData: CmsActivityLineData;
    public cmsCurrenctCycle: CmsCurrentCycle;
    public cmsSegmentSwitch: CmsSegmentSwitch;
    public cmsMyAccount: CmsMyAccount;
    public cmslineCardData: CmsLineCardData;
    public cmsPlanOptions: CmsPlanOptionsFeatures;
    public cmsChangePending: CmsActivityChangePending;
    public errorBanner: CmsActivityChangePending;
    public manageUserLink: {
        text: string;
        url: string;
        cbmUrl: string;
    };
    public geminiInternationalServices: {
        header: string;
        subtitle: string;
        body: string;
    };

    public searchRatesUrl: string;
    public addLineIconOptions: MediaImageOptions;
    public planOptionsIcon: MediaImageOptions;
    public changePendingIcon: MediaImageOptions;

    public accountSummary: AccountSummary;
    public planEligibilityPromise: Promise<void>;
    public estimatedStatement: Transaction;
    public plans: PlanSummary[];

    public hasChangePending: boolean;
    public hasActivityStatus: boolean;
    public hasAPIError: boolean;
    public isMobile: boolean;
    public canAddLines: boolean;
    public isBusinessUser: boolean;
    public changePendingCallText: CbmResiChangePendingCallAndText;
    public initialLoadLimit: number = LINES_LIST_FIRST_LOAD_LIMIT;
    public enableIcopPhaseOnePlanSwitch: boolean;
    public showSortFilter: boolean = false;
    public apiFailureModalData: DescriptiveModalData;
    public isSeg5: boolean = false;
    public isSeg6: boolean = false;
    // eslint-disable-next-line @typescript-eslint/typedef
    public UnlPlanSubgroup = UnlPlanSubgroup;
    public planSevenUNLDataPlan: CmsPlanSevenUNLDataPlans[];
    public isLoading: boolean = true;
    public enableLineLevelExperience: boolean = false;
    public isExtendedUserRolesResi: boolean = false;
    public isMemberViewer: boolean = false;
    public isPrimary: boolean = false;
    public isManager: boolean  = false;
    public enableLineLevelExperienceCBM: boolean = false;
    public showSegmentSwitchBanner: boolean = false;
    public header: string;

    public isGtpLoading: boolean = true;
    public isInternational: boolean = false;
    public isSeg6ResiUser: boolean;
    public enableCbmNoLevel: boolean = false;
    public isNoLevel: boolean = false;

    public promotionsBanner: BillingBanner;
    public infoIconGrey: MediaImageOptions;

    public globalTravelPassHeader: string;
    public gtpTabName: string = GlobalTravelPassTabName.GLOBAL;

    public globalTab: GlobalTravelPassTab;
    public internationalTab: GlobalTravelPassTab;
    public mexCanTab: GlobalTravelPassTab;

    public gtpPrice: string;
    public mexCanPrice: string;
    public gtpCtaLabel: string;

    // Images for Global Travel Pass and Mexico/Canada Pass
    public globalTravelPassImage: MediaImageOptions;
    public mexicoCanadaImage: MediaImageOptions;
    public inUseIcon: MediaImageOptions;
    public manageUserUrl: string;

    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;
    private xmStore: XmStore;
    private state: StateService;
    private decorator: Decorator;
    private config: IXMOptions;
    private mobileDetect: MobileDetect = new MobileDetect(navigator.userAgent);
    private modal: Modal;
    private scroll: Scroll;
    private xmEnvLinkPipe: XmEnvLinkPipe;

    constructor(cimaCore: CimaCore, decorator: Decorator, xmStore: XmStore, state: StateService, @Inject(CONFIG_TOKEN) config: IXMOptions, modal: Modal, scroll: Scroll, xmEnvLinkPipe: XmEnvLinkPipe) {
        Object.assign(this, { cimaCore, decorator, xmStore, state, config, modal, scroll, xmEnvLinkPipe });
    }

    public ngOnInit(): void {
        this.hasChangePending = this.state.params.hasChangePending;
        this.hasAPIError = this.state.params.hasAPIError;
        this.hasActivityStatus = this.state.params.hasChangePending || this.state.params.hasAPIError;
        this.isMobile = Boolean(this.mobileDetect.mobile());
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isExtendedUserRolesResi = this.config.ENABLE_EXTENDED_USER_ROLES && !this.cimaCore.isBusinessUser;
        this.enableLineLevelExperienceCBM = this.config.ENABLE_CBM_LINE_LEVEL_EXP && this.cimaCore.isBusinessUser;
        this.enableIcopPhaseOnePlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccount>(CmsMyAccount), (cmsMyAccount: CmsMyAccount) => {
            this.cmsMyAccount = cmsMyAccount;
            this.apiFailureModalData = cmsMyAccount.apiFailureModalData;
        }));

        if (this.hasAPIError) {
            this.modal.descriptive(this.apiFailureModalData);
        }

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsActivity>(CmsActivity), (cmsActivity: CmsActivity) => {
            this.cmsLineData = cmsActivity.lineData;
            this.cmsCurrenctCycle = cmsActivity.currentCycle;
            this.cmsSegmentSwitch = cmsActivity.segmentSwitch;
            this.cmslineCardData = cmsActivity.lineCardData;
            this.cmsPlanOptions = cmsActivity.planOptions;
            this.cmsChangePending = cmsActivity.changePending;
            this.errorBanner = cmsActivity.errorBanner;
            this.manageUserLink = cmsActivity.manageUserLink;
            this.geminiInternationalServices = cmsActivity.geminiInternationalServices;
            this.manageUserUrl = this.enableLineLevelExperienceCBM
                ? this.manageUserLink.cbmUrl?.replace('[CBM_PAYMENT_BASE_URL]', this.config.CBM_PAYMENT_BASE_URL) 
                : this.manageUserLink.url?.replace('[CUSTOMER_BASE_URL]', this.config.CUSTOMER_BASE_URL);
            this.globalTravelPassImage = {
                mobile: Cloudinary.generateMediaFromCms(cmsActivity.globalTravelPass.icons.globalTravelPassIcon)
            };

            this.mexicoCanadaImage = {
                mobile: Cloudinary.generateMediaFromCms(cmsActivity.globalTravelPass.icons.mexicoCanadaPassIcon)
            };

            this.inUseIcon = {
                mobile: Cloudinary.generateMediaFromCms(cmsActivity.globalTravelPass.icons.inUseIcon)
            };

            this.gtpPrice = cmsActivity.globalTravelPass.prices.globalTravelPass;
            this.mexCanPrice = cmsActivity.globalTravelPass.prices.mexicoCanadaPass;
            this.gtpCtaLabel = cmsActivity.globalTravelPass.buttonLabels.seeDetailsAndAdd;

            this.changePendingCallText = this.isBusinessUser ? this.cmsChangePending.cbmCustomer : this.cmsChangePending.resiCustomer;

            this.changePendingIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsChangePending.image)
            };

            this.planOptionsIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsPlanOptions.image)
            };
            this.planSevenUNLDataPlan = this.cimaCore.isBusinessUser ? this.cmsLineData.planSevenUNLDataPlanCBM : this.cmsLineData.planSevenUNLDataPlanResi;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            this.plans = this.orderPlanGroups(accountSummary.planGroups);

            let totalLinesCount: number = 0;
            this.plans.forEach((plan: PlanSummary) => {
                totalLinesCount += plan.lines.length;
            });
            if (totalLinesCount > this.initialLoadLimit) {
                this.showSortFilter = true;
            }
            if (this.isBusinessUser) {
                this.plans.forEach((plan: PlanSummary) => {
                    plan.sortedLines = this.getLineIconColors(plan.sortedLines);
                });
            }
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.canAddLines = user.allowedLines.canAddLine;
            this.isSeg5 = user.account.isSegmentFive;
            this.isSeg6 = user.account.isSegmentSix;
            this.isNoLevel = user.account.isNoLevel;
            this.enableCbmNoLevel = this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && user.account.isNoLevel;
            this.isSeg6ResiUser = !this.cimaCore.isBusinessUser && this.isSeg6;
            this.showSegmentSwitchBanner = user.account.canMoveLevel || ((user.account.isSegmentFive && !this.cimaCore.isBusinessUser)
                && !user.account.isNYCustomer);
            this.isMemberViewer = user.account.isMember || user.account.isViewer;
            this.isPrimary = user.account.isPrimary;
            this.isManager = user.account.isManager;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cmsCore: CmsCore) => {
            this.addLineIconOptions = {
                mobile: Cloudinary.generateMediaFromCms(cmsCore.icons.circlePlusIcon)
            };
        }));

        const gtpAction: StoreAction = this.isSeg6ResiUser ? StoreAction.GET_GLOBAL_TRAVEL_PASS_PLAN_EIGHT : StoreAction.GET_GLOBAL_TRAVEL_PASS;
        if (!this.enableCbmNoLevel) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<GlobalTravelPass>(gtpAction, GlobalTravelPass),
                (gtp: GlobalTravelPass) => {
                    this.isInternational = gtp.isInternational;
                    if (this.isInternational && gtp.tabs.length > 0) {
                        this.internationalTab = gtp.tabs.find((tab: GlobalTravelPassTab) => tab.pass === GlobalTravelPassTabName.INTERNATIONAL);
                    }
                    this.globalTravelPassHeader = gtp.header;
                    this.globalTab = gtp.tabs.length > 0 && gtp.tabs[0];
                    this.mexCanTab = gtp.tabs.length > 0 && gtp.tabs[1];
                    this.isGtpLoading = false;

                    if (this.state.params.tab) {
                        this.gtpTabName = this.state.params.tab === 'mexcan' ? GlobalTravelPassTabName.MEXICO_CANADA : GlobalTravelPassTabName.GLOBAL;
                        this.scroll.toHash('addons-section');
                    }
                }, () => {
                    this.isGtpLoading = false;
                }
            ));
        } else {
            this.globalTravelPassHeader = this.geminiInternationalServices.header;
            this.isGtpLoading = false;
        }

        this.subscriptions.push(XmStoreUtil.subscribe(
            this.xmStore.find<BillingBannerInfo>(StoreAction.GET_BILLING_BANNER, BillingBannerInfo), (bannerInfo: BillingBannerInfo) => {
                if (bannerInfo && bannerInfo.banners) {
                    this.promotionsBanner = bannerInfo.banners.find((banner: BillingBanner) => banner.location === 'activityBtgLines');
                }
            }
        ));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cms: CmsCore) => {
            this.infoIconGrey = {
                mobile: Cloudinary.generateMediaFromCms(cms.icons.infoIconGreySmall)
            };
        }));

        if (!this.isMemberViewer) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<TransactionCollection>(StoreAction.GET_TRANSACTIONS_BY_PARAMS, TransactionCollection), (transactionCollection: TransactionCollection) => {
                this.isLoading = false;
                this.estimatedStatement = transactionCollection.estimatedTransaction;
            }, () => {
                this.isLoading = false;
            }));
        }
    }

    public openSwitchDataModal(): void {
        this.planEligibilityPromise = this.xmStore.fetchPromise<AccountPlanEligibility>(StoreAction.GET_ACCOUNT_PLAN_ELIGIBILITY)
            .then(() => {
                this.state.go('account.activity.bucket-switch.bucket-picker');
            });
    }

    public getPlanHeader(plan: PlanSummary): string {
        if (!this.enableCbmNoLevel) {
            this.header = plan.lines.length > 1 ? this.cmsLineData.multiLinePlanHeader : this.cmsLineData.singleLinePlanHeader;
        } else {
            this.header = plan.lines.length > 1 ? this.cmsLineData.cbmNoLevelMultiLineHeader : this.cmsLineData.cbmNoLevelSingleLineHeader;
        }

        switch (plan.planType) {
            case DataPlanType.BY_THE_GIG:
                return this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.byTheGig);
            case DataPlanType.BY_THE_GIG_SHARED:
                const byTheGigSharedDescription: string = this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.byTheGig);

                return !(this.isSeg5) ? `${byTheGigSharedDescription}, ${Util.mbToGb(plan.dataPassSize, true)} GB` : `${byTheGigSharedDescription}`;
            case DataPlanType.UNLIMITED:
                return this.enableCbmNoLevel? this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.cbmNoLevelSmartPhone) : this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.unlimited);
            case DataPlanType.SMART_WATCH:
                if (plan.lines.length > 1) {
                    return this.cmsLineData.multiWatchHeader.replace('[NUMBER]', plan.lines.length.toString());
                }

                return this.cmsLineData.singleWatchHeader;
            case DataPlanType.TABLET:
                return this.enableCbmNoLevel? this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.cbmNoLevelTablet) : this.header.replace('[NUMBER]', plan.lines.length.toString())
                    .replace('[PLAN_NAME]', this.cmsMyAccount.plans.tablet);
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public getShopUrl(): string {
        if (this.isBusinessUser) {
            return this.config.ENABLE_CBM_XOE_SHOP_URL ? this.decorator.createCrossAppUrl(this.cmsLineData.cbmXOEShopURL) : this.decorator.createCrossAppUrl(this.cmsLineData.cbmShopURL);
        } else {
            if (this.config.ENABLE_BUYFLOW_CONVERGENCE_URL) {
                return this.decorator.createCrossAppUrl(this.cmsLineData.resiShopBuyFLowURL);
            }

            return this.decorator.createCrossAppUrl(this.cmsLineData.resiShopURL);
        }
    }

    public getLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.sortedLines : plan.sortedLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getIntroLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlIntroLines : plan.unlIntroLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getPlusLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlPlusLines : plan.unlPlusLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getPremiumLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlPremiumLines : plan.unlPremiumLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getProLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlProLines : plan.unlProLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getStandardLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlStandardLines : plan.unlStandardLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public getSelectLinesList(plan: PlanSummary): LineSummary[] {
        const linesList: LineSummary[] = plan.showAllLines ? plan.unlSelectLines : plan.unlSelectLines.slice(0, this.initialLoadLimit);

        return linesList;
    }

    public toggleLineList(plan: PlanSummary): void {
        plan.showAllLines = !plan.showAllLines;
    }

    // eslint-disable-next-line complexity
    public getShowLinesLabel(plan: PlanSummary, planSubgroup?: string): string {
        // eslint-disable-next-line @typescript-eslint/typedef
        let label;
        if (this.accountSummary.level5Customer && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.BASIC) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlIntroLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.accountSummary.level5Customer && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.PLUS) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlPlusLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.accountSummary.level5Customer && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.PREMIUM) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlPlusLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.enableCbmNoLevel && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.PRO) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlProLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.enableCbmNoLevel && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.STANDARD) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlStandardLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.accountSummary.level6Customer && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.SELECT) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlSelectLines.length - this.initialLoadLimit).toString());

            return label;
        }
        if (this.accountSummary.level6Customer && plan.isUnlimited && planSubgroup === UnlPlanSubgroup.PLUS) {
            label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.unlPlusLines.length - this.initialLoadLimit).toString());

            return label;
        }
        label = plan.showAllLines ? this.cmsLineData.showLessButon : this.cmsLineData.showMoreButton.replace('[REMAINING_LINES]', (plan.lines.length - this.initialLoadLimit).toString());

        return label;
    }

    public dropDownChange(sortOptions: DropdownOutput): void {
        this.plans.forEach((plan: PlanSummary) => {
            plan.sortedLines = plan.sort(sortOptions.selection.value);
        });
    }

    public getLineIconColors(lines: LineSummary[]): LineSummary[] {
        let colorIndex: number = 0;

        for (const line of lines) {
            const pairedLine: LineSummary = line.isActive && line.pairedLines.length && this.accountSummary.getLineByKey(line.pairedLines[0].lineId.key);
            // check if line count reach the color palette for color (If it reaches reset the count)
            colorIndex = colorIndex === AccountSummary.colors.length ? 0 : colorIndex;
            line.color = pairedLine && pairedLine.color ? pairedLine.color : AccountSummary.colors[colorIndex++];
        }

        return lines;
    }

    public getUnlimitedPlanName(lines: LineSummary[]): string {
        let matchedPlanName: string | null = undefined;
        lines.some((line: LineSummary) =>
            this.planSevenUNLDataPlan.some((dataPlan: CmsPlanSevenUNLDataPlans) => {
                if (dataPlan.planID === line.planId) {
                    matchedPlanName = dataPlan.planName;

                    return true;
                }

                return false;
            })
        );

        return matchedPlanName;
    }

    public onGtpCtaClick(): void {
        if (this.gtpTabName === GlobalTravelPassTabName.GLOBAL) {
            this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.GTP, intent: 'add' });
        } else if (this.gtpTabName === GlobalTravelPassTabName.MEXICO_CANADA) {
            this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.MEX_CAN_PASS, intent: 'add' });
        }
    }

    public onSearchTravelRatesBtnClick(): void {
        const url: string = !this.isBusinessUser ? this.cmsLineData.resilink : this.cmsLineData.cbmlink;
        this.searchRatesUrl = this.xmEnvLinkPipe.transform(url);
        window.open(this.searchRatesUrl, '_blank', 'noopener');
    }

    public onGtpOptionBtnClick(event: GtpLineOptionClick): void {
        if (event.option === 'See details') {
            if (event.lineId) {
                this.state.go('account.activity.details', { line_id: event.lineId });
            }
        } else if (event.option === 'Remove') {
            if (this.gtpTabName === GlobalTravelPassTabName.GLOBAL) {
                this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.GTP, intent: 'remove' });
            } else if (this.gtpTabName === GlobalTravelPassTabName.MEXICO_CANADA) {
                this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.MEX_CAN_PASS, intent: 'remove' });
            }
        } else if (event.option === 'See details and add') {
            if (this.gtpTabName === GlobalTravelPassTabName.GLOBAL) {
                this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.GTP, intent: 'add' });
            } else if (this.gtpTabName === GlobalTravelPassTabName.MEXICO_CANADA) {
                this.state.go('account.activity.addon.review', { addon_type: AddonRouteType.MEX_CAN_PASS, intent: 'add' });
            }
        }
    }

    public onAddonsScrollAction(action?: string): void {
        if (action === BannerAction.SCROLL_TO_CANMEX) {
            this.gtpTabName = GlobalTravelPassTabName.MEXICO_CANADA;
        } else if (action === BannerAction.SCROLL_TO_GTP) {
            this.gtpTabName = GlobalTravelPassTabName.GLOBAL;
        }
    }

    private orderPlanGroups(planGroups: PlanSummary[]): PlanSummary[] {
        const sortedPlanGroups: PlanSummary[] = [];
        const planTypes: string[] = [DataPlanType.BY_THE_GIG, DataPlanType.BY_THE_GIG_SHARED, DataPlanType.UNLIMITED, DataPlanType.TABLET, DataPlanType.SMART_WATCH];

        for (const planType of planTypes) {
            const planGroup: PlanSummary = planGroups.find((plan: PlanSummary) => plan.planType === planType);
            if (planGroup) {
                sortedPlanGroups.push(planGroup);
            }
        }

        return sortedPlanGroups;
    }
}
